import * as R from 'ramda';

export default function RecommendationCategoryController(
  $q,
  $rootScope,
  $scope,
  alerts,
  amDataService,
  amSharedModelService,
  confirmDialog,
  editRecommendationCategoryDialog,
  editRecommendationDialog,
  loaderService,
  orgDataService,
  ssDialogService,
) {
  const vm = this;

  // Set Default Values
  vm.canExpand = true;
  vm.isExpanded = false;

  vm.canEditItem = vm.recommendationCategory.canEdit && vm.auditIsActive;
  vm.canDeleteItem = vm.recommendationCategory.canDelete && vm.auditIsActive;

  // Attach Event Handlers
  vm.handleExpandRecommendationCategory = _handleExpandRecommendationCategory;
  vm.handleAddRecommendation = _handleAddRecommendation;
  vm.handleEditRecommendationCategory = _handleEditRecommendationCategory;
  vm.handleDeleteRecommendationCategory = _handleDeleteRecommendationCategory;
  vm.handleConfirmDelete = _handleConfirmDelete;
  vm.handleCancelDelete = _handleCancelDelete;
  vm.handleDeletedRecommendation = _handleDeletedRecommendation;

  _initEventListeners();
  _activate();

  /** **********************************************
   * Event Handlers
   */

  function _handleExpandRecommendationCategory() {
    if (!vm.isExpanded) {
      _loadRecommendations().then(() => {
        vm.isExpanded = true;
      });
    } else {
      vm.isExpanded = false;
    }
  }

  function _handleAddRecommendation($event) {
    return amDataService.getDefaultRecommendation(vm.recommendationCategory.recommendationCategoryId).then((response) => {
      ssDialogService.dialogs.auditTrail.editRecommendationDialog = ssDialogService.alert(
        editRecommendationDialog.getOptions($event, {
          customerId: vm.customerId,
          auditId: vm.auditId,
          businessRisks: vm.businessRisks,
          technicalRisks: vm.technicalRisks,
          priorities: vm.priorities,
          statuses: vm.statuses,
          users: vm.users,
          recommendation: response.data,
          addCallback: _handleAddedRecommendation,
        }),
      );
      return ssDialogService.show(ssDialogService.dialogs.auditTrail.editRecommendationDialog);
    });
  }

  function _handleEditRecommendationCategory($event) {
    return amDataService.getRecommendationCategory(vm.recommendationCategory.recommendationCategoryId).then(() => {
      ssDialogService.dialogs.auditTrail.editRecommendationCategoryDialog = ssDialogService.alert(
        editRecommendationCategoryDialog.getOptions($event, {
          recommendationCategory: vm.recommendationCategory,
        }),
      );
      return ssDialogService.show(ssDialogService.dialogs.auditTrail.editRecommendationCategoryDialog);
    });
  }

  function _handleDeleteRecommendationCategory($event) {
    ssDialogService.dialogs.auditTrail.deleteRecommendationCategoryConfirmDialog = ssDialogService.alert(
      confirmDialog.getOptions($event, {
        dialogTitle: 'Delete Recommendation Category?',
        message: `Are you sure you want to delete Recommendation Category "${vm.recommendationCategory.name}"?`,
        handleYesClick: _handleConfirmDelete,
        handleNoClick: _handleCancelDelete,
      }),
    );
    return ssDialogService.show(ssDialogService.dialogs.auditTrail.deleteRecommendationCategoryConfirmDialog);
  }

  function _handleConfirmDelete() {
    loaderService.showViewLoader = true;
    return amDataService.deleteRecommendationCategory(vm.recommendationCategory.recommendationCategoryId)
      .then(() => {
        alerts.success('The Recommendation Category has been deleted successfully.');
        $rootScope.$broadcast('refreshAMRecommendationCategories', { auditId: vm.auditId });
        _closeDeleteDialog();
      }).catch(() => {
        alerts.error('Error occurred while deleting the Recommendation Category.');
      }).finally(() => {
        loaderService.showViewLoader = false;
      });
  }

  function _handleCancelDelete() {
    _closeDeleteDialog();
  }

  function _handleDeletedRecommendation(recommendation) {
    if (!recommendation) return;
    const pos = vm.recommendations.findIndex(item => item.recommendationId === recommendation.recommendationId);
    if (pos === -1) return; // Can't find the item
    vm.recommendations.splice(pos, 1);
  }

  /** **********************************************
   * Private Functions
   */

  function _activate() {
    const dfdActivate = $q.defer();

    _initEventListeners();
    _refreshBusinessRisks().then(
      _refreshTechnicalRisks().then(
        _refreshPriorities().then(
          _refreshRecommendationStatuses().then(
            _refreshUsers().then(
              dfdActivate.resolve,
            ),
          ),
        ),
      ),
    );
  }

  function _initEventListeners() {
    $scope.$on('refreshAMBusinessRisks', _refreshBusinessRisks);
    $scope.$on('refreshAMTechnicalRisks', _refreshTechnicalRisks);
    $scope.$on('refreshAMPriorities', _refreshPriorities);
    $scope.$on('refreshAMUsers', _refreshUsers);

    $scope.$on('refreshAMRecommendations', (event, opt) => {
      if (opt.recommendationCategoryId === vm.recommendationCategory.recommendationCategoryId) {
        _loadRecommendations();
      }
    });

    $scope.$on('refreshAMRecommendationCategory', (event, opt) => {
      if (opt.recommendationCategoryId === vm.recommendationCategory.recommendationCategoryId) {
        _refreshRecommendationCategory();
        _loadRecommendations();
      }
    });
  }

  function _handleAddedRecommendation(recommendation) {
    vm.recommendations = vm.recommendations || [];
    vm.recommendations.push(recommendation);
    if (vm.recommendations.length > 1) {
      vm.recommendations = amSharedModelService.sortByName(vm.recommendations);
    }
    _setRecommendationAttributes();
  }

  function _refreshRecommendationCategory() {
    return amDataService.getRecommendationCategory(vm.recommendationCategory.recommendationCategoryId).then((response) => {
      vm.recommendationCategory = response.data;
    });
  }

  function _loadRecommendations() {
    loaderService.showViewLoader = true;
    return amSharedModelService.getRecommendations(vm.recommendationCategory.recommendationCategoryId).then((recommendations) => {
        loaderService.showViewLoader = false;
        vm.recommendations = recommendations;
        _setRecommendationAttributes();
      });
  }

  function _setRecommendationAttributes() {
    R.forEach((recommendation) => {
      recommendation.canAddItemNote = (recommendation.canAddNote || recommendation.isAssignedToMe) && vm.auditIsActive;
      recommendation.canAttachItemItems = (recommendation.canAttachItems || recommendation.isAssignedToMe) && vm.auditIsActive;

      recommendation.canChangeItemStatus = (recommendation.canChangeStatus || recommendation.isAssignedToMe || recommendation.canEdit)
        && recommendation.statusId !== 3 && vm.auditIsActive;

      recommendation.canEditItem = recommendation.canEdit && vm.auditIsActive;
      recommendation.canDeleteItem = recommendation.canDelete && vm.auditIsActive;
    }, vm.recommendations);
  }

  function _refreshBusinessRisks() {
    return amSharedModelService.getBusinessRisks().then((businessRisks) => {
      vm.businessRisks = businessRisks;
    });
  }

  function _refreshTechnicalRisks() {
    const dfdRefresh = $q.defer();

    vm.technicalRisks = amSharedModelService.technicalRisks;
    dfdRefresh.resolve();

    return dfdRefresh.promise;
  }

  function _refreshPriorities() {
    const dfdRefresh = $q.defer();

    vm.priorities = amSharedModelService.priorities;
    dfdRefresh.resolve();

    return dfdRefresh.promise;
  }

  function _refreshUsers() {
    const dfdRefresh = $q.defer();

    vm.users = amSharedModelService.applicationUsers;
    dfdRefresh.resolve();

    return dfdRefresh.promise;
  }

  function _refreshRecommendationStatuses() {
    const dfdRefresh = $q.defer();

    vm.statuses = amSharedModelService.recommendationStatuses;
    dfdRefresh.resolve();

    return dfdRefresh.promise;
  }

  function _closeDeleteDialog() {
    return ssDialogService.hide(ssDialogService.dialogs.auditTrail.deleteRecommendationCategoryConfirmDialog);
  }
}

RecommendationCategoryController.$inject = [
  '$q',
  '$rootScope',
  '$scope',
  'alerts',
  'amDataService',
  'amSharedModelService',
  'confirmDialog',
  'editRecommendationCategoryDialog',
  'editRecommendationDialog',
  'loaderService',
  'orgDataService',
  'ssDialogService',
];
